import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import type { RechargeFormData } from '../operations/types';

export default function PointsRechargePage() {
  const [formData, setFormData] = useState<RechargeFormData>({
    tenantName: '',
    rechargeAmount: 0,
    operator: '当前用户',
    remark: '',
    hasExpiry: false,
    effectiveTime: '',
    expiryTime: '',
    usagePeriod: 0
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.tenantName.trim()) {
      newErrors.tenantName = '请选择租户名称';
    }

    if (!formData.rechargeAmount || formData.rechargeAmount <= 0) {
      newErrors.rechargeAmount = '请输入充值数量';
    }

    if (formData.hasExpiry) {
      if (!formData.effectiveTime) {
        newErrors.effectiveTime = '请选择生效时间';
      }
      if (!formData.expiryTime) {
        newErrors.expiryTime = '请选择过期时间';
      }
      if (!formData.usagePeriod || formData.usagePeriod <= 0) {
        newErrors.usagePeriod = '请输入使用期限';
      }
      if (formData.effectiveTime && formData.expiryTime) {
        const effectiveDate = new Date(formData.effectiveTime);
        const expiryDate = new Date(formData.expiryTime);
        if (expiryDate <= effectiveDate) {
          newErrors.expiryTime = '过期时间必须晚于生效时间';
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    alert('充值成功！');
    window.REACT_APP_NAVIGATE('/points/operations');
  };

  const handleCancel = () => {
    window.REACT_APP_NAVIGATE('/points/operations');
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">积分充值</h1>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="max-w-3xl">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  租户名称 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.tenantName}
                  onChange={(e) => {
                    setFormData({ ...formData, tenantName: e.target.value });
                    setErrors({ ...errors, tenantName: '' });
                  }}
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    errors.tenantName ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">请选择租户</option>
                  <option value="阳光小学">阳光小学</option>
                  <option value="明德中学">明德中学</option>
                  <option value="华夏大学">华夏大学</option>
                  <option value="启明小学">启明小学</option>
                  <option value="博文中学">博文中学</option>
                  <option value="育才高中">育才高中</option>
                  <option value="实验小学">实验小学</option>
                  <option value="文华中学">文华中学</option>
                </select>
                {errors.tenantName && (
                  <p className="mt-1 text-sm text-red-500">{errors.tenantName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  充值数量 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.rechargeAmount || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, rechargeAmount: Number(e.target.value) });
                    setErrors({ ...errors, rechargeAmount: '' });
                  }}
                  placeholder="请输入充值数量"
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                    errors.rechargeAmount ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.rechargeAmount && (
                  <p className="mt-1 text-sm text-red-500">{errors.rechargeAmount}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">操作人员</label>
                <input
                  type="text"
                  value={formData.operator}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">备注信息</label>
                <textarea
                  value={formData.remark}
                  onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                  placeholder="请输入备注信息"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  是否有效期 <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.hasExpiry}
                      onChange={() => setFormData({ ...formData, hasExpiry: true })}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">是</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={!formData.hasExpiry}
                      onChange={() => setFormData({ ...formData, hasExpiry: false })}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">否</span>
                  </label>
                </div>
              </div>

              {formData.hasExpiry && (
                <div className="pl-6 border-l-2 border-teal-500 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      生效时间 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.effectiveTime}
                      onChange={(e) => {
                        setFormData({ ...formData, effectiveTime: e.target.value });
                        setErrors({ ...errors, effectiveTime: '' });
                      }}
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                        errors.effectiveTime ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.effectiveTime && (
                      <p className="mt-1 text-sm text-red-500">{errors.effectiveTime}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      过期时间 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.expiryTime}
                      onChange={(e) => {
                        setFormData({ ...formData, expiryTime: e.target.value });
                        setErrors({ ...errors, expiryTime: '' });
                      }}
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                        errors.expiryTime ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.expiryTime && (
                      <p className="mt-1 text-sm text-red-500">{errors.expiryTime}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      使用期限（天） <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.usagePeriod || ''}
                      onChange={(e) => {
                        setFormData({ ...formData, usagePeriod: Number(e.target.value) });
                        setErrors({ ...errors, usagePeriod: '' });
                      }}
                      placeholder="请输入使用期限"
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                        errors.usagePeriod ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.usagePeriod && (
                      <p className="mt-1 text-sm text-red-500">{errors.usagePeriod}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 flex items-center gap-3">
              <button
                onClick={handleSubmit}
                className="px-6 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                确认充值
              </button>
              <button
                onClick={handleCancel}
                className="px-6 py-2 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
